/**
 * Modal frame for timesheet/expense detail pages.
 *
 * UX rules:
 *   - ESC closes when not dirty.
 *   - ESC opens a "Änderungen verwerfen?" confirm when dirty.
 *   - Click on backdrop = same behavior as ESC.
 *   - Close button (✕) = same behavior as ESC.
 *   - Body scroll is locked while the modal is open so the underlying board
 *     can't scroll. The board's own scroll position is preserved naturally
 *     (it stays mounted under the overlay).
 *
 * Direct-link case (no background location): callers should NOT render this
 * frame — render the page contents standalone.
 */
import { useEffect, useState, useRef, type ReactNode } from "react";
import { useNavigate, useLocation, type Location } from "react-router-dom";

interface DetailModalProps {
  children: ReactNode;
  dirty: boolean;
  onDiscard?: () => void;
  /** When set, called instead of `navigate(-1)`. Useful for save+close flows. */
  onClose?: () => void;
}

export function DetailModal({ children, dirty, onDiscard, onClose }: DetailModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const background = (location.state as { background?: Location } | null)?.background;
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Body-scroll lock: prevents the underlying board from scrolling while the
  // modal is open. Restored on unmount.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const close = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (background) {
      navigate(background, { replace: false });
    } else {
      navigate(-1);
    }
  };

  const requestClose = () => {
    if (showDiscardPrompt) return;
    if (dirty) {
      setShowDiscardPrompt(true);
      return;
    }
    close();
  };

  // Global ESC handler — bound at document level so focus inside any input
  // still triggers it. We don't preventDefault so cell-input ESC still works
  // for the input's own clear-on-escape.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Only handle when no inner dialog/popover is shown (we have one: the
      // discard prompt) — that one consumes ESC itself.
      if (showDiscardPrompt) return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dirty, showDiscardPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-outside: clicking the backdrop closes; clicks inside the dialog
  // bubble up to the same handler but we filter by target.
  const onBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) requestClose();
  };

  return (
    <div className="detail-modal-backdrop" onClick={onBackdropClick} role="presentation">
      <div className="detail-modal" ref={dialogRef} role="dialog" aria-modal="true">
        <button
          type="button"
          className="detail-modal-close"
          onClick={requestClose}
          title="Schließen (Esc)"
          aria-label="Schließen"
        >
          ✕
        </button>
        <div className="detail-modal-body">{children}</div>
      </div>
      {showDiscardPrompt && (
        <DiscardChangesPrompt
          onDiscard={() => {
            setShowDiscardPrompt(false);
            onDiscard?.();
            close();
          }}
          onCancel={() => setShowDiscardPrompt(false)}
        />
      )}
    </div>
  );
}

function DiscardChangesPrompt({
  onDiscard,
  onCancel,
}: {
  onDiscard: () => void;
  onCancel: () => void;
}) {
  // Inner dialog has its own ESC handler — ESC cancels the discard prompt
  // (back to the editing view), it does NOT close the outer modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  return (
    <div className="discard-confirm-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="discard-confirm">
        <h4>Änderungen verwerfen?</h4>
        <p className="muted">Du hast nicht gespeicherte Änderungen. Beim Schließen gehen sie verloren.</p>
        <div className="discard-confirm-actions">
          <button type="button" onClick={onCancel}>Weiter bearbeiten</button>
          <button type="button" className="danger" onClick={onDiscard}>Verwerfen + schließen</button>
        </div>
      </div>
    </div>
  );
}
